import {createPortal} from "react-dom";
import {Box, IconButton, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {CallEnd01Icon, Cancel01Icon, Mic01Icon, Mic02Icon, VolumeHighIcon, VolumeOffIcon} from "@hugeicons/core-free-icons";
import {VoiceOrbGlow} from "./VoiceOrbGlow";
import LiquidEther from "./LiquidEther";

const DEFAULT_STATUS_LABELS = {
  idle: "Ready",
  listening: "Listening…",
  thinking: "Thinking…",
  speaking: "Speaking…",
  error: "Something went wrong",
};

// Ambient backdrop palette per call state — hex stops for LiquidEther's color ramp. idle/
// listening/error all stay on the landing page's brand purple (error used to be red; changed
// to primary so a failed call doesn't clash with the rest of the brand-purple UI); thinking/
// speaking keep their own accent so those states still read as visually distinct. Defined once
// at module scope (not inline in the JSX) so each array keeps a stable reference across
// renders — LiquidEther rebuilds its whole WebGL scene when `colors` changes, which is fine on
// a real call-state transition but shouldn't happen on every render.
const PRIMARY_BACKDROP = ["#2A2566", "#6258E8", "#8B7CFF"];
const STATE_BACKDROP_COLORS = {
  idle: PRIMARY_BACKDROP,
  listening: PRIMARY_BACKDROP,
  thinking: ["#2A2566", "#6258E8", "#8B7CFF"],
  speaking: ["#123C42", "#2E96A8", "#58C7E8"],
  error: PRIMARY_BACKDROP,
};

/**
 * A rounded "glass" control button — subtle blur + translucency, distinct red for the
 * destructive end-call action, a filled look while `active` (e.g. currently muted).
 */
function GlassButton({children, active, tone, ...props}) {
  return (
    <IconButton
      {...props}
      sx={{
        width: 58,
        height: 58,
        color: "#fff",
        bgcolor: tone === "danger" ? "rgba(229,72,77,0.92)" : active ? "rgba(255,255,255,0.28)" : "rgba(255,255,255,0.1)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        border: "1px solid rgba(255,255,255,0.14)",
        transition: "background-color 160ms ease, transform 160ms ease",
        "&:hover": {bgcolor: tone === "danger" ? "rgba(199,52,56,0.95)" : "rgba(255,255,255,0.22)", transform: "translateY(-1px)"},
        "&:active": {transform: "translateY(0)"},
      }}
    >
      {children}
    </IconButton>
  );
}

/**
 * Full-screen, premium "AI voice assistant" conversation UI — a ChatGPT-voice-mode-style
 * layout: title, a dominant animated VoiceOrb, a one-line status, and rounded glass controls
 * pinned to the bottom. Renders into a portal covering the viewport, with a dark, blurred
 * (not solid) backdrop so it reads as a modal rather than a page swap.
 *
 * Deliberately independent of any particular backend: hand it a `state` and whichever live
 * audio sources you have, and wire `onToggleMute` / `onEndCall` / `onToggleSpeaker` to your
 * own call/voicebot logic. Omitting a handler hides that control.
 */
export function VoiceAssistantScreen({
  state = "idle",
  audioStream = null,
  audioElement = null,
  theme,
  title = "AI Assistant",
  statusLabels,
  // Overrides the state-keyed label below with one exact line for whatever's actually
  // happening right now (e.g. "Registering…", "Calling…") — a caller with finer-grained
  // lifecycle info than the 5 orb states (idle/listening/thinking/speaking/error) can surface
  // it here instead of being stuck with the generic per-state text. Falls back to `statusLabels`
  // when omitted or empty.
  statusText,
  errorMessage,
  muted = false,
  onToggleMute,
  onEndCall,
  speakerMuted = false,
  onToggleSpeaker,
  // Placing the call is wired to the Ready orb's tap — VoiceAssistantScreen only passes it
  // through while state === "idle" below, so it's inert once dialing/the call itself has
  // started (tapping the live orb mid-call shouldn't re-trigger a second call attempt). A
  // caller can also fire this itself right as the screen opens for an auto-started call.
  onStart,
}) {
  const labels = {...DEFAULT_STATUS_LABELS, ...statusLabels};

  return createPortal(
    <Box
      role="dialog"
      aria-modal="true"
      aria-label={title}
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 3,
        bgcolor: "rgba(12, 11, 22, 0.72)",
        backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
        color: "#fff",
        textAlign: "center",
        px: 3,
        pt: "max(28px, env(safe-area-inset-top))",
        pb: "max(24px, env(safe-area-inset-bottom))",
        overflow: "hidden",
        animation: "voScreenIn 220ms ease",
        "@keyframes voScreenIn": {from: {opacity: 0}, to: {opacity: 1}},
      }}
    >
      {/* Ambient backdrop: a slow-drifting fluid animation instead of a flat blurred backdrop,
          tinted per call state. `zIndex: -1` (this Box is already a stacking context via
          position:fixed above) keeps it behind the title/orb/controls without needing to touch
          their own z-index; `pointerEvents: none` keeps it from swallowing clicks on controls
          that sit above empty parts of it. `lightMode={false}` (LiquidEther's default) is what
          makes its own background render fully transparent — that's what lets the dialog's own
          dark blur (`bgcolor`/`backdropFilter` above) show through everywhere the fluid isn't.
          `mouseInteractive={false}` keeps this purely ambient — driven only by autoDemo, never
          by (or paused by) the real cursor sitting over the modal. */}
      <Box aria-hidden sx={{position: "absolute", inset: 0, zIndex: -1, opacity: 0.6, pointerEvents: "none"}}>
        <LiquidEther
          colors={STATE_BACKDROP_COLORS[state] || STATE_BACKDROP_COLORS.idle}
          mouseInteractive={false}
          autoDemo
          autoSpeed={0.35}
          autoIntensity={1.8}
          autoResumeDelay={0}
          autoRampDuration={1}
          resolution={0.5}
        />
      </Box>

      <Stack alignItems="center" sx={{width: "100%", position: "relative", minHeight: 32}}>
        <Typography sx={{fontSize: 13, letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(255,255,255,0.55)", fontWeight: 600}}>
          {title}
        </Typography>
        {onEndCall && (
          <IconButton
            onClick={onEndCall}
            aria-label="Close"
            sx={{position: "absolute", right: 0, top: -10, color: "rgba(255,255,255,0.65)", "&:hover": {color: "#fff"}}}
          >
            <HugeiconsIcon icon={Cancel01Icon} />
          </IconButton>
        )}
      </Stack>

      <Stack alignItems="center" gap={3.5} sx={{flex: 1, justifyContent: "center"}}>
        <VoiceOrbGlow
          state={state}
          audioStream={audioStream}
          audioElement={audioElement}
          size={240}
          backgroundColor="#fefefe"
          lockToBrandHue
          forceHoverState={true}
          onStart={state === "idle" ? onStart : undefined}
        />
        <Stack alignItems="center" gap={0.75}>
          <Typography sx={{fontSize: 21, fontWeight: 600}}>{statusText || labels[state] || labels.idle}</Typography>
          {state === "error" && errorMessage && (
            <Typography sx={{fontSize: 13, color: "rgba(255,167,167,0.9)", maxWidth: 320}}>{errorMessage}</Typography>
          )}
        </Stack>
      </Stack>

      <Stack direction="row" gap={2.25} sx={{mb: 0.5}}>
        {onToggleMute && (
          <GlassButton onClick={onToggleMute} active={muted} aria-label={muted ? "Unmute microphone" : "Mute microphone"}>
            {muted ? <HugeiconsIcon icon={Mic02Icon} /> : <HugeiconsIcon icon={Mic01Icon} />}
          </GlassButton>
        )}
        {onEndCall && (
          <GlassButton onClick={onEndCall} tone="danger" aria-label="End conversation">
            <HugeiconsIcon icon={CallEnd01Icon} />
          </GlassButton>
        )}
        {onToggleSpeaker && (
          <GlassButton onClick={onToggleSpeaker} active={speakerMuted} aria-label={speakerMuted ? "Unmute speaker" : "Mute speaker"}>
            {speakerMuted ? <HugeiconsIcon icon={VolumeOffIcon} /> : <HugeiconsIcon icon={VolumeHighIcon} />}
          </GlassButton>
        )}
      </Stack>
    </Box>,
    document.body
  );
}
